// Read-only transcript drawer for the Merlin Visibility Pane.
//
// A right slide-over that shows one conversation exactly as the Agent Console
// renders it (same `buildTranscriptTimeline` + `AssistantTurn` + cards), plus
// what is unique to this pane: the external media the thread is held over and
// every message that crossed them, verbatim. Presentational; the page fetches.

import { useEffect, useMemo } from 'react';

import {
  buildTranscriptTimeline,
  formatCostUsd,
  formatTokens,
  type ConversationDetail,
} from '../../services/agentConsole';
import type { ConversationChannelsRead, MonitorRow } from '../../services/merlinMonitor';
import type { AttachmentDownloader, ChainDetail } from '../../types/portal';
import EmailChainThread from '../portal/EmailChainThread';
import { AssistantTurn } from '../agentConsole/AssistantTurnView';
import { MessageTimestamp } from '../agentConsole/MessageTimestamp';
import {
  ConversationDecisionCard,
  SentMessageCard,
} from '../agentConsole/ConversationMessageCard';
import { AgentFileCard } from '../agentConsole/AgentFileCard';
import { formatDateTime } from '../../utils/dateFormatters';
import { formatMoney } from '../../utils/formatting';
import { ChannelChips, StateBadge, StreamBadge } from './StreamBadge';
import { channelTitle, rowTitle, slackRoomLabel } from './monitorDisplay';
import styles from './TranscriptDrawer.module.css';

export interface TranscriptDrawerProps {
  row: MonitorRow | null;
  detail: ConversationDetail | null;
  channels: ConversationChannelsRead | null;
  /** The Gmail thread a Tech Support Co-Pilot conversation is bound to
   *  (null when the row has no thread, or while it is still loading). */
  emailThread?: ChainDetail | null;
  /** Attachment bytes for the bound thread's mail (the Tech Support mailbox
   *  reads live from Gmail; omit to use the orders-portal path). */
  downloadAttachment?: AttachmentDownloader;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

export function TranscriptDrawer({
  row,
  detail,
  channels,
  emailThread = null,
  downloadAttachment,
  loading,
  error,
  onClose,
}: TranscriptDrawerProps) {
  const open = row !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const timeline = useMemo(
    () => (detail ? buildTranscriptTimeline(detail.messages, detail.tool_calls) : []),
    [detail],
  );

  if (!row) return null;

  // A courier thread (a data request) never runs a model: the channel log IS
  // the conversation, so the transcript section is expected to be empty.
  const isCourier = row.stream === 'data_request';

  const crossings = (channels?.bindings ?? []).flatMap((b) =>
    b.messages.map((m) => ({ binding: b, message: m })),
  );
  crossings.sort((a, b) => a.message.occurred_at.localeCompare(b.message.occurred_at));

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} data-testid="drawer-backdrop" />
      <aside className={styles.drawer} role="dialog" aria-label="Conversation transcript" data-testid="transcript-drawer">
        <header className={styles.head}>
          <div className={styles.headMain}>
            <div className={styles.badges}>
              <StreamBadge stream={row.stream} label={row.stream_label} />
              <StateBadge state={row.state} />
              <ChannelChips channels={row.channels} />
            </div>
            <h2 className={styles.title}>{rowTitle(row)}</h2>
            <p className={styles.meta}>
              {row.profile_name ?? 'Unknown agent'} · started by{' '}
              <strong>{row.owner_name ?? row.owner_email ?? '—'}</strong> ·{' '}
              {formatDateTime(row.created_at)} · last active {formatDateTime(row.last_activity_at)}
            </p>
            <p className={styles.meta}>
              {isCourier ? (
                <>Courier thread · no model runs here · the messages above are the exchange</>
              ) : (
                <>
                  {row.model} · {row.assistant_turns} replies · {row.tool_call_count} tool calls ·{' '}
                  {(row.input_tokens + row.output_tokens).toLocaleString()} tokens ·{' '}
                  {formatMoney(row.cost_usd)}
                </>
              )}
              {row.feedback_up + row.feedback_down > 0 && (
                <>
                  {' · '}👍 {row.feedback_up} 👎 {row.feedback_down}
                </>
              )}
            </p>
            {row.error_message && <p className={styles.err}>⚠ {row.error_message}</p>}
          </div>
          <div className={styles.headActions}>
            <a
              className={styles.consoleLink}
              href={`/agent-console?conversation=${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Agent Console ↗
            </a>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {(channels?.bindings.length ?? 0) > 0 && (
            <section className={styles.channels} data-testid="drawer-channels">
              <h3 className={styles.sectionHead}>Over the wire</h3>
              <ul className={styles.bindingList}>
                {channels?.bindings.map((b) => (
                  <li key={b.id} className={styles.binding}>
                    <span className={styles.bindingKind}>{b.channel.toUpperCase()}</span>
                    <span className={styles.bindingWho} title={channelTitle(b)}>
                      {b.counterpart_label ? `${b.counterpart_label} · ` : ''}
                      {b.counterpart_address}
                    </span>
                    <span className={styles.bindingAnchor}>
                      about {b.anchor_kind.replace(/_/g, ' ')} {b.anchor_id}
                    </span>
                    <span className={styles.bindingCount}>
                      {b.messages.length} {b.messages.length === 1 ? 'message' : 'messages'}
                    </span>
                  </li>
                ))}
              </ul>
              {crossings.length > 0 && (
                <ol className={styles.crossings}>
                  {crossings.map(({ binding, message }) => (
                    <li
                      key={message.id}
                      className={`${styles.crossing} ${message.direction === 'inbound' ? styles.inbound : styles.outbound}`}
                    >
                      <div className={styles.crossingMeta}>
                        <span className={styles.dir}>
                          {message.direction === 'inbound' ? '⬅ inbound' : '➡ outbound'}
                        </span>
                        <span>{binding.channel.toUpperCase()}</span>
                        {message.counterpart && <span>{message.counterpart}</span>}
                        <span>{formatDateTime(message.occurred_at)}</span>
                        {slackRoomLabel(message, binding.channel) && (
                          <span className={styles.room} data-testid="crossing-room">
                            {slackRoomLabel(message, binding.channel)}
                          </span>
                        )}
                      </div>
                      {message.subject && <div className={styles.subject}>{message.subject}</div>}
                      <pre className={styles.bodyText}>{message.body_text}</pre>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}

          {row.email_thread_id && (
            <section className={styles.channels} data-testid="drawer-email-thread">
              <h3 className={styles.sectionHead}>
                Email thread
                {emailThread && emailThread.email_count > 0
                  ? ` · ${emailThread.email_count} ${emailThread.email_count === 1 ? 'message' : 'messages'}`
                  : ''}
              </h3>
              {!emailThread && loading && <p className={styles.note}>Loading the email thread…</p>}
              {emailThread && emailThread.email_count === 0 && (
                <p className={styles.note}>
                  This conversation is bound to Gmail thread {row.email_thread_id}, but no
                  synced mail is on file for it.
                </p>
              )}
              {emailThread && emailThread.email_count > 0 && (
                <EmailChainThread
                  emails={emailThread.emails}
                  downloadAttachment={downloadAttachment}
                />
              )}
            </section>
          )}

          <section className={styles.transcript} data-testid="drawer-transcript">
            <h3 className={styles.sectionHead}>Transcript</h3>
            {loading && !detail && <p className={styles.note}>Loading transcript…</p>}
            {error && <p className={styles.errNote}>{error}</p>}
            {detail && timeline.length === 0 && (
              <p className={styles.note}>
                {isCourier
                  ? 'No model turns on a courier thread. Everything Merlin and the person said is in the Over the wire section above.'
                  : 'No messages yet.'}
              </p>
            )}
            {timeline.map((item) =>
              item.kind === 'user' ? (
                <div key={item.message.id} className={`${styles.bubble} ${styles.user}`}>
                  <p className={styles.userText}>{item.message.content}</p>
                  <div className={styles.bubbleMeta}>
                    <MessageTimestamp at={item.message.created_at} />
                  </div>
                </div>
              ) : (
                <div key={item.message.id} className={`${styles.bubble} ${styles.assistant}`}>
                  <AssistantTurn blocks={item.blocks} isStreaming={false} feedback={null} />
                  {item.sentMessages?.map((sent) => (
                    <SentMessageCard key={sent.key} message={sent} />
                  ))}
                  {item.decisions?.map((decision) => (
                    <ConversationDecisionCard key={decision.key} decision={decision} />
                  ))}
                  {item.agentFiles?.map((file) => (
                    <AgentFileCard
                      key={file.attachmentId}
                      base="/admin/agent-console"
                      file={file}
                      conversationId={row.id}
                    />
                  ))}
                  <div className={styles.bubbleMeta}>
                    {formatTokens(item.message) ? (
                      <>
                        {formatTokens(item.message)}
                        {item.message.cost_usd != null ? ` · ${formatCostUsd(item.message.cost_usd)}` : ''}
                        {item.message.model ? ` · ${item.message.model}` : ''}
                        {' · '}
                      </>
                    ) : null}
                    <MessageTimestamp at={item.message.created_at} />
                  </div>
                </div>
              ),
            )}
          </section>
        </div>
      </aside>
    </>
  );
}
